import React from 'react';
import NavigationDrawerWide from './NavigationDrawerWide';

import { RouteProps } from 'react-router-dom';

import { envReady, getEnvVar } from '../../../../utils/getEnvVar';
import { useFeatures } from '@cube3/state/src/redux/components/Hooks/useFeatures';
import { ModalNames } from '../Modals/modalIndex';
import { useWorkspacePermissions } from '@cube3/state/src/redux/components/Hooks/usePermission';
import { useMemo } from 'react';
import { Feature } from '@cube3/common/model/resource-types';
import { urlStructureProjectOrList } from '../../routing/routingPaths';
import QuickNavigationDrawer from './QuickNavigationDrawer';
import { MediaQueueContextProvider } from '../MediaQueue/context/mediaQueueContext';
import { NavItem } from '@cube3/cubicle/src/core/atoms/Navigation/NavItem';
import { isMobile } from 'react-device-detect';
import { useTrialWorkspaceCheck } from '../AdminCenter/hooks/useTrialWorkspaceCheck';

/// @Author : Simon @ AmbassadorsLAB
/// <Summary>
/// Layout component on the left side of the screen that can be used for searching
/// Cube and navigation to pages within cube.
/// </Summary>


export const policiesItems = {
  groupName: 'policies',
  items: [
    {
      linkTitle: 'Contact us',
      url: undefined
    },
    {
      linkTitle: 'Terms and Policies',
      url: '/legal/policies/'
    },
    {
      linkTitle: 'Cookie settings',
      modal: 'cookie_settings' as ModalNames
    }
  ],
  alignBottom: true
};


envReady.promise.then(() => {
  policiesItems.items.find(i => i.linkTitle === 'Contact us').url = getEnvVar('CONTACT_US_URL');
})


interface NavDrawerProperties {
  rootUrl: string;
  isAdmin: boolean;
  variant:
  | 'account-settings'
  | 'workspace'
  | 'privacy'
  | 'adminCenter'
  | 'quickNavigation';
  backUrl?: string;
  //hide search when its not needed
  hideSearch?: boolean;
}

export interface NavDrawerItem extends NavItem {
  modal?: ModalNames;
  modalData?: unknown;
  matchUrl?: string | RouteProps;
}

interface NavigationItem {
  groupName: string;
  title?: string;
  items: NavDrawerItem[];
  alignBottom?: boolean;
}

const requiredFeatures = {
  features: [
    '/WORKSPACE/SHARING/SHARE_LINKS',
    '/WORKSPACE/SHARING/FILE_REQUESTS',
    '/WORKSPACE/SHARING/REVIEW_LINKS',
    '/WORKSPACE/SHARING/REVIEWS',
    '/WORKSPACE/CONTRACTS',
    '/WORKSPACE/ADMINCENTER',
    '/WORKSPACE/EXPORTS/YOUTUBE',
    '/WORKSPACE/TEAMS',
    '/WORKSPACE/LIBRARY',
    '/WORKSPACE/PROJECTS',
    '/WORKSPACE/FAVORITES',
    '/WORKSPACE/FORMS'
  ] as Feature[]
};

const NavigationDrawer: React.FC<NavDrawerProperties> = React.memo((props) => {
  const {
    isAdmin,
    rootUrl,
    backUrl = '/',
    variant,
    hideSearch = false
  } = props;
  const [
    sharelinkFeature,
    fileRequestFeature,
    reviewLinkFeature,
    newReviewFeature,
    contractsFeature,
    adminCenterFeature,
    youtubeFeature,
    teamsFeature,
    libraryFeature,
    projectsFeature,
    favoriteFeature,
    formBuilderFeature
  ] = useFeatures(requiredFeatures);

  const [
    canReadContracts,
    canReadProjects,
    canCreateShares,
    canWriteLibrary,
    canReadLibrary,
    canWriteWorkspace
  ] = useWorkspacePermissions([
    'CONTRACT_READ',
    'PROJECT_READ',
    'SHARE_LINK',
    'LIBRARY_WRITE',
    'LIBRARY_READ',
    'WORKSPACE_WRITE'
  ]);

  const { isTrial, hasExpired } = useTrialWorkspaceCheck();

  const settingsItems: NavigationItem[] = useMemo(
    () => [
      {
        groupName: 'settings',
        items: [
          ...(!isMobile
            ? [
              {
                imagePath: 'chevron_left' as const,
                linkTitle: 'Back',
                url: backUrl,
                matchUrl: { path: backUrl, exact: true }
              }
            ]
            : []),
          {
            imagePath: 'person',
            linkTitle: 'Account',
            url: rootUrl + 'general',
            matchUrl: rootUrl + 'general'
          },
          {
            imagePath: 'encrypted',
            linkTitle: 'Password & authentication',
            url: rootUrl + 'password-and-authentication',
            matchUrl: rootUrl + 'password-and-authentication'
          },
          {
            imagePath: 'notifications',
            linkTitle: 'Notifications',
            url: rootUrl + 'notifications',
            matchUrl: rootUrl + 'notifications'
          },
          {
            imagePath: 'signal',
            linkTitle: 'Sessions',
            url: rootUrl + 'sessions',
            matchUrl: rootUrl + 'sessions'
          },
          {
            imagePath: 'workspaces',
            linkTitle: 'Workspaces',
            url: rootUrl + 'workspaces',
            matchUrl: rootUrl + 'workspaces'
          },
          {
            imagePath: 'accounts',
            linkTitle: 'Connected accounts',
            url: rootUrl + 'connected-accounts',
            matchUrl: rootUrl + 'connected-accounts'
          },
          {
            imagePath: 'shield_person',
            linkTitle: 'Privacy',
            url: '/legal/policies/'
          }
        ]
      }
    ],
    [backUrl, rootUrl]
  );

  const privacyItems: NavigationItem[] = useMemo(
    () => [
      {
        groupName: 'privacy',
        items: [
          ...(!isMobile
            ? [
              {
                imagePath: 'chevron_left' as const,
                linkTitle: 'Back',
                url: backUrl,
                matchUrl: { path: backUrl, exact: true }
              }
            ]
            : []),
          {
            imagePath: 'cookie',
            linkTitle: 'Cookie Statement',
            url: rootUrl + 'cookie-statement'
          },
          {
            imagePath: 'settings',
            linkTitle: 'Cookie settings',
            modal: 'cookie_settings' as ModalNames,
            onClick: (ev, openModal) => {
              ev.stopPropagation();
              openModal('cookie_settings');
            }
          },
          {
            imagePath: 'contract',
            linkTitle: 'Privacy Policy',
            url: rootUrl + 'privacy-policy'
          },
          {
            imagePath: 'contract',
            linkTitle: 'Terms of Agreement',
            url: rootUrl + 'terms-of-agreement'
          }
        ]
      }
    ],
    [rootUrl, backUrl]
  );

  const workspaceItems: NavigationItem[] = useMemo(
    () => [
      {
        groupName: 'main',
        items: [
          canReadProjects && projectsFeature
            ? {
              imagePath: 'project',
              linkTitle: 'Projects',
              url: rootUrl + 'projects',
              matchUrl: { path: urlStructureProjectOrList }
            }
            : undefined,
          canReadLibrary && libraryFeature
            ? {
              imagePath: 'view_list',
              linkTitle: 'Library',
              url: rootUrl + 'libraries',
              matchUrl: rootUrl + 'library'
            }
            : undefined,

          favoriteFeature
            ? {
              imagePath: 'star',
              linkTitle: 'Favorites',
              url: rootUrl + 'favorites',
              matchUrl: rootUrl + 'favorites'
            }
            : undefined,
          canCreateShares && sharelinkFeature
            ? {
              imagePath: 'link',
              linkTitle: 'Shared',
              url: rootUrl + 'shared'
            }
            : undefined,
          fileRequestFeature && canWriteLibrary
            ? {
              imagePath: 'inbox',
              linkTitle: 'File requests',
              url: rootUrl + 'filerequests',
              matchUrl: rootUrl + 'filerequests'
            }
            : undefined,
          reviewLinkFeature && canCreateShares
            ? {
              imagePath: 'chat_bubble',
              linkTitle: 'Review links',
              url: rootUrl + 'review-links',
              matchUrl: rootUrl + 'review-links'
            }
            : undefined,
          newReviewFeature && canCreateShares
            ? {
              imagePath: 'chat_bubble',
              linkTitle: 'Reviews',
              url: rootUrl + 'reviews',
              matchUrl: rootUrl + 'reviews'
            }
            : undefined,
          contractsFeature && canReadContracts
            ? {
              imagePath: 'contract',
              linkTitle: 'Contracts',
              url: rootUrl + 'contracts',
              matchUrl: rootUrl + 'contracts'
            }
            : undefined,

          {
            imagePath: 'delete',
            linkTitle: 'Bin',
            url: rootUrl + 'bin'
          },
          !adminCenterFeature
            ? {
              imagePath: 'person_settings',
              linkTitle: 'Members',
              // url: {path:  `/workspace/${workspaceId}/settings/members`, exact: true},
              modal: 'workspace_members' as const,
              modalData: (navdrawerProps, item, matchPath) => {
                const match = matchPath(
                  navdrawerProps.location.pathname,
                  '/workspace/:workspaceId'
                );
                const ws = match ? match.params.workspaceId : undefined;
                return { workspaceId: ws };
              }
            }
            : undefined,
          adminCenterFeature && canWriteWorkspace
            ? {
              linkTitle: 'Admin Center',
              url: rootUrl + 'admin-center',
              imagePath: 'person_settings',
              shortcut: '`'
            }
            : undefined
          // NOTE: disabled until this is no longer just readonly
          // {
          //   imagePath: iconSettings,
          //   linkTitle: 'Settings',
          //   // url: {path: `/workspace/${workspaceId}/settings/`, exact: true}
          //   modal: 'workspace_settings',
          //   modalData: (navdrawerProps, item, matchPath) => {
          //     let match = matchPath(navdrawerProps.location.pathname, '/workspace/:workspaceId');
          //     const ws = match ? match.params.workspaceId : undefined;
          //     return ws;
          //   }
          // }
        ]
          .filter((i) => i)
          .map(addNumberShortcuts)
      }

      //  policiesItems

      // {
      //   imagePath: iconProjects={
      //   linkTitle: 'Projects',
      //   url: workspaceRootUrl +={
      // },

      // {
      //   imagePath: iconCollecti={
      //   linkTitle: 'Collections={
      //   url: workspaceRootUrl +={
      // },
    ],
    [
      rootUrl,
      canCreateShares,
      canReadContracts,
      canReadLibrary,
      canReadProjects,
      canWriteWorkspace,
      canWriteLibrary,
      contractsFeature,
      sharelinkFeature,
      fileRequestFeature,
      reviewLinkFeature,
      adminCenterFeature,
      libraryFeature,
      projectsFeature,
      favoriteFeature
    ]
  );

  const quickNavigationItems: NavigationItem[] = useMemo(() => {
    const mobileMenuModal: ModalNames = rootUrl.includes('workspace')
      ? 'mobile_menu_workspace'
      : rootUrl.includes('account')
        ? 'mobile_menu_accountSettings'
        : 'mobile_menu_privacy';
    return [
      {
        groupName: 'main',
        items: [
          canReadProjects && projectsFeature
            ? {
              imagePath: 'project',
              linkTitle: 'Projects',
              url: rootUrl + 'projects',
              matchUrl: { path: urlStructureProjectOrList }
            }
            : {
              imagePath: 'chevron_left',
              linkTitle: 'Back',
              url: backUrl,
              matchUrl: { path: backUrl, exact: true },
              shortcut: '`'
            },

          favoriteFeature
            ? {
              imagePath: 'star',
              linkTitle: 'Favorites',
              url: rootUrl + 'favorites',
              matchUrl: rootUrl + 'favorites'
            }
            : undefined,
          canCreateShares && sharelinkFeature
            ? {
              imagePath: 'link',
              linkTitle: 'Shared',
              url: rootUrl + 'shared'
            }
            : undefined,
          hideSearch
            ? undefined
            : {
              imagePath: 'search',
              linkTitle: 'Search',
              url: rootUrl + 'search'
            },
          {
            imagePath: 'menu',
            linkTitle: 'Menu',
            modal: mobileMenuModal
          }
        ]
          .filter((i) => i)
          .map(addNumberShortcuts)
      }
    ];
  }, [
    rootUrl,
    canCreateShares,
    canReadProjects,
    sharelinkFeature,
    projectsFeature,
    favoriteFeature
  ]);

  const adminCenterItems: NavigationItem[] = useMemo(
    () => [
      {
        groupName: 'nav',
        items: [
          {
            imagePath: 'chevron_left',
            linkTitle: 'Back to workspace',
            url: backUrl,
            matchUrl: { path: backUrl, exact: true },
            shortcut: '`'
          }
        ]
      },
      {
        groupName: 'main',
        title: 'Admin Center',
        items:
          isTrial && hasExpired
            ? [
              {
                linkTitle: 'Plan & Billing',
                url: rootUrl + 'plan-and-billing',
                matchUrl: rootUrl + 'plan-and-billing',
                imagePath: 'contract_edit_fill'
              }
            ]
            : [
              {
                linkTitle: 'General',
                url: rootUrl + 'general',
                matchUrl: rootUrl + 'general',
                imagePath: 'settings'
              },
              {
                linkTitle: 'Project templates',
                url: rootUrl + 'project-templates',
                matchUrl: rootUrl + 'project-templates',
                imagePath: 'template'
                //   extraSVGProps: { svgProps: { viewBox: '0 24 24' } }
              },
              {
                imagePath: 'label',

                linkTitle: 'Tag management',
                url: rootUrl + 'tag-management',
                matchUrl: rootUrl + 'tag-management'
              },
              {
                linkTitle: 'Members',
                url: rootUrl + 'members',
                matchUrl: rootUrl + 'members',
                imagePath: 'group'
              },
              teamsFeature
                ? {
                  linkTitle: 'Teams',
                  url: rootUrl + 'teams',
                  matchUrl: rootUrl + 'teams',
                  imagePath: 'groups'
                }
                : undefined,
              youtubeFeature
                ? {
                  linkTitle: 'Integrations',
                  url: rootUrl + 'integrations',
                  matchUrl: rootUrl + 'integrations',
                  imagePath: 'extension'
                }
                : undefined,
              formBuilderFeature && {
                linkTitle: 'Forms',
                url: rootUrl + 'forms',
                matchUrl: rootUrl + 'forms',
                imagePath: 'edit'
              },
              {
                linkTitle: 'Plan & Billing',
                url: rootUrl + 'plan-and-billing',
                matchUrl: rootUrl + 'plan-and-billing',
                imagePath: 'contract_edit_fill'
              }
            ]
              .filter((i) => i)
              .map(addNumberShortcuts)
      }
      // {
      //   groupName: 'users',
      //   title: 'Users',

      //   items: [
      //     {
      //       // imagePath: iconPeopleTeams,
      //       linkTitle: 'Members',
      //       url: rootUrl + 'members',
      //       matchUrl: rootUrl + 'members',
      //       imagePath: iconPeopleTeams
      //     }
      //     {
      //       linkTitle: 'Teams',
      //       url: rootUrl + 'teams',
      //       matchUrl: rootUrl + 'teams'
      //     }
      //   ]
      // },
      // policiesItems
      // {
      //   groupName: 'projects',
      //   title: 'Projects',
      //   items: [
      //     {
      //       linkTitle: 'Projects',
      //       url: rootUrl + 'projects',
      //       matchUrl: rootUrl + 'projects'
      //     },
      //     {
      //       linkTitle: 'Metadata',
      //       url: rootUrl + 'metadata',
      //       matchUrl: rootUrl + 'metadata'
      //     },
      //     {
      //       linkTitle: 'Contracts',
      //       url: rootUrl + 'contracts',
      //       matchUrl: rootUrl + 'contracts'
      //     }
      //   ]
      // },
      // {
      //   groupName: 'other',
      //   title: 'Other',

      //   items: [
      //     {
      //       linkTitle: 'Applications',
      //       url: rootUrl + 'applications',
      //       matchUrl: rootUrl + 'applications'
      //     },
      //     {
      //       linkTitle: 'Security & Identity',
      //       url: rootUrl + 'security-and-identity',
      //       matchUrl: rootUrl + 'security-and-identity'
      //     },
      //     {
      //       linkTitle: 'Plan & Billing',
      //       url: rootUrl + 'plan-and-billing',
      //       matchUrl: rootUrl + 'plan-and-billing'
      //     }
      //   ]
      // }
    ],
    [
      backUrl,
      isTrial,
      hasExpired,
      rootUrl,
      teamsFeature,
      formBuilderFeature,
      youtubeFeature
    ]
  );

  let navItemsConfig;

  let displayQuickNav = false;

  switch (variant) {
    case 'account-settings':
      navItemsConfig = settingsItems;
      break;

    case 'privacy':
      navItemsConfig = privacyItems;
      break;

    case 'adminCenter':
      navItemsConfig = adminCenterItems;
      break;

    case 'quickNavigation':
      // navigation menu on mobiles
      navItemsConfig = quickNavigationItems;
      displayQuickNav = true;
      break;

    case 'workspace':
    default:
      navItemsConfig = workspaceItems;
  }

  return (
    <>
      {!displayQuickNav ? (
        <span>
          <NavigationDrawerWide
            isAdmin={isAdmin}
            navigationItems={navItemsConfig}
          />
        </span>
      ) : (
        <MediaQueueContextProvider>
          <QuickNavigationDrawer
            isAdmin={isAdmin}
            navigationItems={navItemsConfig}
          />
        </MediaQueueContextProvider>
      )}
    </>
  );
});

export default NavigationDrawer;

const addNumberShortcuts = (i, idx) => ({ ...i, shortcut: `${idx + 1}` });
